const formatters = new Map<string, Intl.NumberFormat>()

const getFormatter = (key: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
    let formatter = formatters.get(key)

    if (!formatter) {
        formatter = new Intl.NumberFormat('en-US', options)
        formatters.set(key, formatter)
    }

    return formatter
}

interface CurrencyOptions {
    decimals?: number
    compact?: boolean
    sign?: boolean
}

export const formatCurrency = (value: number, { decimals = 0, compact = false, sign = false }: CurrencyOptions = {}): string => {
    const formatter = getFormatter(`currency-${decimals}-${compact}-${sign}`, {
        style: 'currency',
        currency: 'USD',
        notation: compact ? 'compact' : 'standard',
        minimumFractionDigits: compact ? 0 : decimals,
        maximumFractionDigits: compact ? 1 : decimals,
        signDisplay: sign ? 'exceptZero' : 'auto'
    })

    return formatter.format(Number.isFinite(value) ? value : 0)
}

export const formatNumber = (value: number, decimals = 0): string => {
    const formatter = getFormatter(`number-${decimals}`, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })

    return formatter.format(Number.isFinite(value) ? value : 0)
}

export const formatPercent = (value: number, decimals = 1): string => `${formatNumber(value, decimals)}%`

export const formatDuration = (totalMonths: number): string => {
    if (!Number.isFinite(totalMonths)) return 'never at this pace'

    const months = Math.max(0, Math.round(totalMonths))
    const years = Math.floor(months / 12)
    const rest = months % 12
    const parts: Array<string> = []

    if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`)
    if (rest > 0 || years === 0) parts.push(`${rest} ${rest === 1 ? 'month' : 'months'}`)

    return parts.join(' ')
}

export const formatTime = (time: string): string => {
    const [hourText, minuteText = '00'] = time.split(':')
    const hour = Number(hourText)

    if (!Number.isFinite(hour)) return time

    const suffix = hour >= 12 ? 'PM' : 'AM'
    const display = hour % 12 === 0 ? 12 : hour % 12

    return `${display}:${minuteText} ${suffix}`
}
